import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import { useDataProvider, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Create } from "@/components/admin/create";
import { SaveButton } from "@/components/admin/form";
import { FormToolbar } from "@/components/admin/simple-form";
import { MeetingFormBody, MeetingFormCreate } from "./MeetingForm";
import type { CrmDataProvider } from "../providers/types";

/**
 * MeetingCreate — pantalla full para capturar una junta manualmente.
 *
 * Soporta deep-link con state:
 *   navigate('/meetings/create', { state: { record: { company_id, deal_id } } })
 *
 * Después de guardar:
 *   1. Crea la fila en `meetings` (con audio_url, attachments, etc.)
 *   2. Llama dataProvider.setMeetingAttendees(meetingId, attendee_ids)
 *   3. Invalida el cache de meetings_summary
 *   4. Redirige a la vista detalle de la junta nueva
 */
export function MeetingCreate() {
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();

  const presetRecord = (location.state as any)?.record ?? {};

  const onSuccess = async (data: any, variables: any) => {
    const attendeeIds: number[] = variables?.data?.attendee_ids ?? [];
    try {
      await dataProvider.setMeetingAttendees(data.id, attendeeIds);
    } catch (e) {
      notify((e as Error).message, { type: "warning" });
    }
    // Invalida cache para refrescar listas y vista detalle
    await queryClient.invalidateQueries({ queryKey: ["meetings"] });
    await queryClient.invalidateQueries({ queryKey: ["meetings_summary"] });
    notify("Junta guardada", { type: "success" });
    navigate(`/meetings/${data.id}/show`);
  };

  return (
    <div className="px-7 pt-6 pb-7">
      <div className="flex items-center gap-3 mb-5">
        <Link to="/meetings" className="musco-btn musco-btn-g">
          <ArrowLeft size={14} /> Juntas
        </Link>
        <h1 className="m-0 text-[28px] font-bold tracking-[-0.025em]">
          Nueva junta
        </h1>
      </div>

      <div
        className="musco-hero"
        style={{ maxWidth: 920, marginInline: "auto" }}
      >
        <Create
          resource="meetings"
          mutationOptions={{ onSuccess }}
          redirect={false}
        >
          <MeetingFormCreate
            fromCompanyId={presetRecord.company_id}
            fromDealId={presetRecord.deal_id}
          >
            <MeetingFormBody
              fromCompanyId={presetRecord.company_id}
              fromDealId={presetRecord.deal_id}
            />
            <FormToolbar>
              <SaveButton label="Guardar junta" />
            </FormToolbar>
          </MeetingFormCreate>
        </Create>
      </div>
    </div>
  );
}
